/**
 * Keyword normalisation rule.
 *
 * Mirrors `normalizeKeyword` / `normalizeKeywords` in packages/types/src/backend/keywords/keyword-normalizer.ts.
 * The CRA build only sees this app's folder, so the rule is duplicated here. Keep both in sync.
 *
 * Users paste CSV rows such as `bay windows;;national`. Only the first `;`-separated segment is the
 * keyword; the rest are CSV columns. The backend applies the same rule, so what the user sees here is
 * exactly what gets stored.
 */
export const KEYWORD_SEGMENT_SEPARATOR = ';';

export type NormalizedKeyword = {
    index: number;
    original: string;
    normalized: string;
    truncated: boolean;
};

export type NormalizeKeywordsResult = {
    keywords: Array<string>;
    truncated: Array<NormalizedKeyword>;
};

/** Splits textarea input into lines. Handles `\r\n` and drops blank lines. */
export function getKeywords(str: string): Array<string> {
    return str.split(/\r?\n/).filter((line) => line.trim().length > 0);
}

export function trimKeywords(keywords: Array<string>) {
    return keywords.map((key) => key.trim());
}

/** First `;`-separated segment, trimmed. `"bay windows;;national"` → `"bay windows"`. */
export function normalizeKeyword(raw: string): string {
    const text = (raw ?? '').toString();
    const separatorIndex = text.indexOf(KEYWORD_SEGMENT_SEPARATOR);
    const segment = separatorIndex === -1 ? text : text.slice(0, separatorIndex);
    return segment.trim();
}

/** Normalises, drops empties and de-duplicates (first occurrence wins). */
export function normalizeKeywords(raw: Array<string>): NormalizeKeywordsResult {
    const seen = new Set<string>();
    const keywords: Array<string> = [];
    const truncated: Array<NormalizedKeyword> = [];

    (raw ?? []).forEach((original, index) => {
        const text = (original ?? '').toString();
        const normalized = normalizeKeyword(text);
        const wasTruncated = text.includes(KEYWORD_SEGMENT_SEPARATOR);

        if (wasTruncated) {
            truncated.push({ index, original: text, normalized, truncated: true });
        }

        if (!normalized || seen.has(normalized)) {
            return;
        }

        seen.add(normalized);
        keywords.push(normalized);
    });

    return { keywords, truncated };
}
